import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaUser, FaEnvelope, FaPhone, FaLock, FaEdit, FaCamera } from 'react-icons/fa';
import { updateProfile } from '../../features/auth/authSlice';

const Profile = () => {
  const { user, isLoading, isError, isSuccess, message } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setAvatarPreview(user.avatar || null);
    }
  }, [user]);

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }

    if (isSuccess) {
      toast.success('Profile updated successfully');
      setEditMode(false);
      setPasswordMode(false);
    }
  }, [isError, isSuccess, message]);

  const { name, email, phone, bio, currentPassword, newPassword, confirmPassword } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    
    const profileData = {
      name,
      bio,
      phone
    };
    
    // Note: Avatar upload is not supported yet in the backend
    if (avatar) {
      toast.info('Avatar upload will be available in a future update');
    }
    
    dispatch(updateProfile(profileData));
  };

  const handlePasswordUpdate = (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    dispatch(updateProfile({
      currentPassword,
      newPassword,
    }));
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">My Profile</h1>
      
      <div className="bg-white container-rounded-lg shadow overflow-hidden">
        {/* Profile header */}
        <div className="bg-primary text-white p-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="relative mb-4 md:mb-0 md:mr-6">
              <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center overflow-hidden">
                {avatarPreview ? (
                  <img 
                    src={avatarPreview} 
                    alt={name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl text-gray-400">{name.charAt(0).toUpperCase()}</span>
                )}
              </div>
              {editMode && (
                <div className="absolute bottom-0 right-0">
                  <label htmlFor="avatar-upload" className="cursor-pointer bg-white text-primary p-2 rounded-full shadow hover:bg-gray-100">
                    <FaCamera />
                    <input 
                      type="file" 
                      id="avatar-upload" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleAvatarChange}
                    />
                  </label>
                </div>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">{name}</h2>
              <p className="text-white text-opacity-80">{email}</p>
              {!editMode && (
                <button 
                  onClick={() => setEditMode(true)}
                  className="mt-2 flex items-center text-sm bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded"
                >
                  <FaEdit className="mr-1" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Profile content */}
        <div className="p-6">
          {editMode ? (
            <form onSubmit={handleProfileUpdate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label">
                    <FaUser className="mr-2" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={onChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <FaEnvelope className="mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    className="form-input bg-gray-100"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <FaPhone className="mr-2" />
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={phone}
                    onChange={onChange}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group md:col-span-2">
                  <label className="form-label">Bio</label>
                  <textarea
                    name="bio"
                    value={bio}
                    onChange={onChange}
                    className="form-input min-h-[100px]"
                    placeholder="Tell us about yourself"
                  ></textarea>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-gray-500 text-sm mb-1">Full Name</h3>
                  <p className="font-medium">{name}</p>
                </div>
                
                <div>
                  <h3 className="text-gray-500 text-sm mb-1">Email Address</h3>
                  <p className="font-medium">{email}</p>
                </div>
                
                <div>
                  <h3 className="text-gray-500 text-sm mb-1">Phone Number</h3>
                  <p className="font-medium">{phone || 'Not provided'}</p>
                </div>
                
                <div className="md:col-span-2">
                  <h3 className="text-gray-500 text-sm mb-1">Bio</h3>
                  <p>{bio || 'No bio provided'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Password section */}
        <div className="border-t p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <FaLock className="mr-2" />
            Password
          </h3>
          
          {passwordMode ? (
            <form onSubmit={handlePasswordUpdate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={currentPassword}
                    onChange={onChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group"></div>
                
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={newPassword}
                    onChange={onChange}
                    className="form-input"
                    required
                    minLength="6"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={onChange}
                    className="form-input"
                    required
                    minLength="6"
                  />
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={() => setPasswordMode(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          ) : (
            <div>
              <p className="text-gray-500 mb-4">Change your password to keep your account secure.</p>
              <button
                onClick={() => setPasswordMode(true)}
                className="btn-secondary"
              >
                Change Password
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;